import React, {useCallback, useEffect, useState, useRef} from 'react';
import { useRequestUrl, useCaptchaKey } from "../context/contextHooks";
import { Turnstile } from '@marsidev/react-turnstile';
import {v4 as uuidv4} from 'uuid';
import { useStyles as classes } from '../styles/styles';
import API from '../http/API';
import { validateEmail } from '../global/utils';
import { useTheme } from "@mui/material/styles";
import useMediaQuery from '@mui/material/useMediaQuery'
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import ReplayIcon from "@mui/icons-material/Replay";
import SendTwoToneIcon from '@mui/icons-material/SendTwoTone';
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import {getCurrency, getDecimal} from "../global/utils";
import Popup from '../layout/Popup';
import {HtmlComponent} from "../global/components";
import Verify from "../completePayment/Verify";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Typography from "@mui/material/Typography";
import Link from "@mui/material/Link";
import ShortTandC from "../layout/ShortTandC";
import Grid from "@mui/material/Grid";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import TextField from "@mui/material/TextField";

// Back-off (seconds) between server-side reconciliation attempts. The number of
// entries is also the maximum number of checkPaymentStatus polls before we give
// up and warn the user NOT to resubmit.
const POLL_DELAYS = [5, 10, 20, 30];

// Shown when the gateway widget rejected before anything left the browser. Every one
// of these says outright that no payment was sent: the user is about to retry, and a
// bare "error" would leave them wondering whether they have already been charged.
const WIDGET_LOADING_MSG = 'The payment form is still loading. Please wait a moment and click Submit again — your payment has not been sent.';
const WIDGET_FAILED_MSG = 'The payment form did not load correctly. Please close this window and try again — your payment has not been sent.';
const FORM_INVALID_MSG = 'Please check the payment information entered above; some required fields are missing or incomplete. Your payment has not been sent.';

const ManageWidget = (props) => {
    const openDialog = props.openDialog;
    const setOpenDialog = props.setOpenDialog;
    const paymentRequest = props.paymentRequest;
    const setPaymentRequest = props.setPaymentRequest;
    const setPaymentResult = props.setPaymentResult
    const cancelPymt = props.cancelPymt;
    const court = props.court;
    const feeResponse = props.feeResponse;
    const [requestUrl,] = useRequestUrl();
    const [captchaKey,] = useCaptchaKey();
    const theme = useTheme();
    const small = useMediaQuery(theme.breakpoints.down('md'));
    const tiny = useMediaQuery(theme.breakpoints.down('sm'));
    const [loading, setLoading] = useState(false);
    const [actionResponse, setActionResponse] = useState(null);
    const [errorMessage, setErrorMessage] = useState(null);
    const [declineMessage, setDeclineMessage] = useState(null);
    const [scriptUrl, setScriptUrl] = useState(null);
    const [bundleResponse, setBundleResponse] = useState(null);
    const [clientId, setClientId] = useState(null);
    const [acceptTerms, setAcceptTerms] = useState(false);
    const [token, setToken] = useState(null);
    const [openNotice, setOpenNotice] = useState(false);
    const [prepaymentShown, setPrepaymentShown] = useState(false);
    const captchaRef = React.useRef();
    const [noSubmit, setNoSubmit] = useState(false);
    const [verifyOpen, setVerifyOpen] = useState(false);
    const [verifyRequired, setVerifyRequired] = useState(null);
    const [openTandC, setOpenTandC] = useState(false);
    const widg = useRef(null);
    const [initStage, setInitStage] = useState('1');
    const [submitStage, setSubmitStage] = useState('None');
    // The payment service found an earlier authorized payment that partly overlaps this one
    // and issued a token anyway (e.g. a further installment). The payer is shown what was
    // found, and the next Submit uses that token rather than asking for another.
    const [overlapConfirm, setOverlapConfirm] = useState(false);
    const [pollTick, setPollTick] = useState(0);
    const [statusUnknown, setStatusUnknown] = useState(false);
    const [widgetReady, setWidgetReady] = useState(false);
    const checkAttemptRef = useRef(0);
    const notReadyRef = useRef(0);

    // A Turnstile token is single use, and the server spends it when it issues the payment
    // token. Dropping it here as well keeps Submit disabled until the widget has produced a
    // fresh one, rather than letting the payer send one the server will refuse.
    const resetCaptcha = () => {
        setToken(null);
        captchaRef.current?.reset();
    }

    const clearMessages = () => {
        setErrorMessage(null);
        setDeclineMessage(null);
        setActionResponse(null);
    }

    const getPrePaymentNoticeComponent = () => {
        const title = 'Pre Payment Notice';
        return (
            <HtmlComponent title={title} component={court.prePaymentNotice} />
        );
    }

    const Widget = useCallback( () => {
        if (!captchaKey) return;
        return (
            <Turnstile
                ref={captchaRef}
                siteKey={captchaKey}
                options={{
                    theme: 'light',
                    size: `${small ? 'compact' : 'normal'}`
                }}
                onSuccess={(token) => setToken(token)}
                onExpire={() => resetCaptcha()}
            />
        );
    }, [captchaKey, small]);

    const getScriptUrl = () => {
        clearMessages();
        setLoading(true);
        const url = `${requestUrl}/getNeumoScriptUrl`;
        API.getRetrieve(url,'getScriptUrl', null, setActionResponse);
    }

    const getVerifyRequired = () => {
        clearMessages();
        setLoading(true);
        const url = `${requestUrl}/getVerifyRequired/${court.courtId}`;
        API.getRetrieve(url,'getVerifyRequired', null, setActionResponse);
    }

    const handleSubmit = () => {
        if (noSubmit) return;
        setErrorMessage(null);
        if (overlapConfirm && bundleResponse) {
            // The payer has seen the overlap notice and chosen to go ahead: this token was
            // issued after the captcha and verification checks, and has never been used.
            setOverlapConfirm(false);
            setDeclineMessage(null);
            setSubmitStage('3');
            return;
        }
        setOverlapConfirm(false);
        setSubmitStage('1');
    }

    // The payment token is requested only here, after the captcha and any SMS verification,
    // and the server checks both before issuing it. Requesting it when the dialog opened let a
    // script take the token and send cards straight to the gateway, skipping both checks.
    const getPaymentToken = () => {
        clearMessages();
        setLoading(true);
        setBundleResponse(null);
        paymentRequest.totalAmount = parseFloat(getDecimal(feeResponse.totalAmount, false));
        paymentRequest.courtAmount = parseFloat(getDecimal(feeResponse.courtAmount, false));
        paymentRequest.feeAmount = parseFloat(getDecimal(feeResponse.citepayFee, false));
        paymentRequest.retryToken = uuidv4();
        paymentRequest.captchaToken = token;
        const url = `${requestUrl}/getPaymentToken`;
        API.postAction(url, JSON.stringify(paymentRequest), "getPaymentToken", null, setActionResponse);
    }

    const handleVerificationReturned = (verifyFields) => {
        const request = {...paymentRequest, ...verifyFields};
        setVerifyOpen(false);
        setPaymentRequest(request);
        setSubmitStage('token');
    }

    const closeTandC = (close) => {
        if (close) {
            setOpenTandC(false);
        }
    }

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpenTandC(true);
        }
    }

    const sleep = (seconds) => {
        const ms = seconds * 1000;
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    // Report what the widget actually did, without touching component state. Everything the
    // widget returns happens in the user's browser, so this is the only way to know how long
    // submit() blocks and what it hands back for the payments that succeed at NeumoPay but
    // never get recorded. Sends an allowlist of fields only -- never the raw response, which
    // is the gateway's shape and may carry payment instrument data.
    const logWidgetEvent = (event, detail) => {
        const data = {
            event: event,
            orderBundleId: bundleResponse?.orderBundleId ?? null,
            orderId: bundleResponse?.orders?.[0]?.orderId ?? null,
            retryToken: paymentRequest?.retryToken ?? null,
            courtId: court?.courtId ?? null,
            stage: submitStage,
            pollAttempt: checkAttemptRef.current,
            ...detail
        };
        API.logEvent(`${requestUrl}/logWidgetEvent`, data);
    }

    const describeSubmitResponse = (response) => {
        if (!response || typeof response !== 'object') {
            return {responseKeys: response === undefined ? 'undefined' : String(response)};
        }
        // A thrown Error carries everything in non-enumerable properties, so both
        // Object.keys() and JSON.stringify() see it as empty. Pull the useful parts
        // out by name before the generic object handling below discards them.
        if (response instanceof Error) {
            return {
                transactionSuccessful: null,
                code: response.name ?? null,
                message: response.message ?? null,
                paymentErrorMessage: null,
                responseKeys: 'Error'
            };
        }
        const resp = {
            transactionSuccessful: response.transactionSuccessful ?? null,
            code: response.code ?? null,
            message: response.message ?? null,
            paymentErrorMessage: response.paymentError?.message ?? null,
            responseKeys: Object.keys(response).join(',')
        };

        if (resp.transactionSuccessful === null && resp.message === null) {
            return JSON.parse(JSON.stringify(response));
        } else return resp;
    }

    // widg.current is the raw <payment-instrument> node, which exists long before the
    // Neumo module upgrades it: testing the ref alone passes while submit() is still
    // undefined. Test for the method itself -- that is the only thing submitPayment needs.
    const widgetCanSubmit = () => typeof widg.current?.submit === 'function';

    const submitPayment = async () => {
        if (bundleResponse && widgetCanSubmit()) {
            setNoSubmit(true);
            const startedAt = Date.now();
            try {
                const submitResponse = await widg.current.submit(bundleResponse.submitToken);
                logWidgetEvent('submitResolved', {
                    elapsedMs: Date.now() - startedAt,
                    ...describeSubmitResponse(submitResponse)
                });
                if (submitResponse?.transactionSuccessful) {
                    storePayment(submitResponse);
                } else if (submitResponse.paymentError || submitResponse.message) {
                    handleError(submitResponse);
                } else {
                    // No definitive success or error from the widget: the charge may
                    // still have gone through at NeumoPay, so confirm with the payment
                    // service rather than silently dropping it.
                    startReconciliation('submit returned no definitive result');
                }
            } catch (error) {
                logWidgetEvent('submitRejected', {
                    elapsedMs: Date.now() - startedAt,
                    ...describeSubmitResponse(error)
                });
                handleError(error);
            }
        } else if (bundleResponse) {
            // The payment details are fine; the widget itself is not usable yet. Nothing
            // has been sent, so this is a plain retry -- never reconciliation. A second
            // failure means the script is not coming at all, not that it needs more time.
            notReadyRef.current += 1;
            logWidgetEvent('widgetNotReady', {message: `submit() unavailable, attempt ${notReadyRef.current}`});
            setErrorMessage(notReadyRef.current > 1 ? WIDGET_FAILED_MSG : WIDGET_LOADING_MSG);
            setNoSubmit(false);
            setSubmitStage('None');
            resetCaptcha();
        } else {
            setErrorMessage('Payment information is not complete; please close this window and reselect what you want to pay');
            setSubmitStage('None');
        }
    }

    const storePayment = (submitResponse) => {
        clearMessages();
        setLoading(true);
        const url = `${requestUrl}/storeWidgetPayment`;
        // The session token came back inside the bundle from getPaymentToken. checkPaymentStatus
        // posts that bundle as-is, so only this call has to pass the token on explicitly.
        const data = {paymentRequest: paymentRequest, submitResponse: submitResponse,
            sessionToken: bundleResponse?.sessionToken ?? null, orderBundleId: bundleResponse?.orderBundleId ?? null};
        API.postAction(url, JSON.stringify(data), 'storeWidgetPayment', null, setActionResponse);
    }

    // The widget embeds the gateway's JSON payload in the tail of its message string.
    // Surface the detail out of it when it parses; the raw message is the fallback.
    // Never throws -- handleError runs inside submitPayment's catch, so a parse failure
    // here would escape as an unhandled rejection and strand the payment.
    const extractErrorDetail = (message) => {
        const idx = message.indexOf('{');
        if (idx < 0) return message;
        const errorJsn = message.substring(idx);
        try {
            return JSON.parse(errorJsn)?.detail ?? errorJsn;
        } catch (e) {
            return message;
        }
    }

    // Failures that never left the browser: the widget's own pre-flight validation, which
    // it prefixes 'CLIENT:', and any JavaScript error thrown before submit() reached the
    // gateway. Both are safe to report and retry, but neither is text a payer should see.
    const describeClientError = (submitResponse, message) => {
        if (submitResponse instanceof TypeError) return WIDGET_FAILED_MSG;
        if (message.toLowerCase().startsWith('client:')) {
            if (message.toLowerCase().includes('form invalid')) return FORM_INVALID_MSG;
            return message.substring(message.indexOf(':') + 1).trim();
        }
        return extractErrorDetail(message);
    }

    const handleError = (submitResponse) => {
        resetCaptcha();
        // A structured gateway decline (paymentError) or a definite widget-side message
        // both tell us the card was NOT charged, so it is safe to report and let the
        // user retry. Only the genuinely ambiguous outcomes -- an "unknown" gateway
        // state, a "try again later", or a bare thrown/network error -- get reconciled:
        // there the payment may have succeeded at NeumoPay, and reporting a plain
        // failure would invite a double-charge or leave a payment unrecorded.
        const declineMsg = submitResponse?.paymentError?.message;
        if (declineMsg) {
            if (declineMsg.toLowerCase().includes('unknown')) {
                startReconciliation('gateway reported an unknown state');
                return;
            }
            setDeclineMessage(declineMsg);
            setNoSubmit(false);
            setSubmitStage('None');
            return;
        }
        // Validation the widget performs before anything reaches the gateway (bad card
        // number, short CVV) arrives here as a message with no paymentError. Reconciling
        // those made the user wait out the whole poll cycle only to be warned that a
        // payment that was never sent might have gone through.
        const message = submitResponse?.message;
        if (message) {
            const lower = message.toLowerCase();
            if (lower.includes('unknown') || lower.includes('again later')) {
                startReconciliation('widget reported an indeterminate state');
                return;
            }
            setErrorMessage(describeClientError(submitResponse, message));
            setNoSubmit(false);
            setSubmitStage('None');
            return;
        }
        startReconciliation('submit failed ambiguously');
    }

    // Begin server-side reconciliation: keep the payment locked (noSubmit) and poll
    // checkPaymentStatus so a charge that succeeded at NeumoPay but whose result never
    // reached the payment service still gets recorded.
    const startReconciliation = (reason) => {
        logWidgetEvent('reconcileStarted', {message: reason});
        checkAttemptRef.current = 0;
        setNoSubmit(true);
        setSubmitStage('4');
    }

    // Called when a reconciliation poll comes back without a definitive result (error
    // or still-pending). Retry with back-off until the attempts are exhausted, then
    // warn the user without inviting a resubmit.
    const handleUnconfirmed = () => {
        if (checkAttemptRef.current < POLL_DELAYS.length) {
            setPollTick(tick => tick + 1);
        } else {
            // Terminal, indeterminate outcome: drop the spinner but LOCK Submit
            // (statusUnknown) so the user cannot re-charge a token whose payment may
            // already have gone through -- the top duplicate-payment source.
            setNoSubmit(false);
            setStatusUnknown(true);
            setSubmitStage('Done');
            setErrorMessage('We could not confirm the status of your payment. It may already have been processed — please do NOT submit again. Contact Customer Service to confirm the status before making another payment.');
        }
    }

    const checkPaymentStatus = async () => {
        const attempt = checkAttemptRef.current;
        checkAttemptRef.current = attempt + 1;
        const delay = POLL_DELAYS[Math.min(attempt, POLL_DELAYS.length - 1)];
        await sleep(delay);
        clearMessages();
        setLoading(true);
        const url = `${requestUrl}/checkPaymentStatus`;
        API.postAction(url, JSON.stringify(bundleResponse), 'checkPaymentStatus', null, setActionResponse);
    }

    useEffect(() => {
        if (actionResponse && loading) {
            const requestType = actionResponse.requestType;
            if (actionResponse.error) {
                if (requestType === 'storeWidgetPayment') {
                    // The card was already charged at NeumoPay but the store relay
                    // never landed (network drop, timeout, etc.). Do NOT report a
                    // failure -- reconcile with the payment service so the payment
                    // still gets recorded.
                    startReconciliation('storeWidgetPayment call failed');
                } else if (requestType === 'checkPaymentStatus') {
                    handleUnconfirmed();
                } else {
                    setNoSubmit(false);
                    setErrorMessage(actionResponse.errorMessage);
                    setSubmitStage('None');
                    resetCaptcha();
                }
            } else if (requestType === 'getScriptUrl') {
                setScriptUrl(actionResponse.data.scriptUrl);
                setClientId(actionResponse.data.clientId);
                setInitStage('Done');
            } else if (requestType === 'getPaymentToken') {
                if (actionResponse.alreadyPaid && !actionResponse.data) {
                    // An earlier attempt for exactly these balances turned out to have been
                    // authorized at the gateway, and the payment service has now recorded it
                    // in full. Show that receipt instead of taking a second payment.
                    setNoSubmit(false);
                    setSubmitStage('Done');
                    setPaymentResult(actionResponse.recoveredPayment);
                    setOpenDialog(false);
                } else if (actionResponse.data) {
                    setBundleResponse(actionResponse.data);
                    if (actionResponse.alreadyPaid) {
                        // Balances only partly overlap the recovered payment (e.g. a further
                        // payment plan installment), so this may well be a legitimate
                        // additional payment -- say what we found and let them decide
                        // before anything is sent.
                        setDeclineMessage(actionResponse.message);
                        setOverlapConfirm(true);
                        setSubmitStage('None');
                    } else {
                        setSubmitStage('3');
                    }
                } else {
                    setErrorMessage('Payment information is not complete; please close this window and reselect what you want to pay');
                    setSubmitStage('None');
                    resetCaptcha();
                }
            } else if (requestType === 'storeWidgetPayment' || requestType === 'checkPaymentStatus') {
                if (actionResponse.authorized) {
                    setNoSubmit(false);
                    setSubmitStage('Done');
                    setPaymentResult(actionResponse);
                    setOpenDialog(false);
                } else if (actionResponse.declineMessage) {
                    setNoSubmit(false);
                    setSubmitStage('Done');
                    setDeclineMessage(actionResponse.declineMessage);
                } else if (requestType === 'storeWidgetPayment') {
                    // Store succeeded but returned neither an authorization nor a
                    // decline: the payment may still be settling at the gateway. Enter
                    // reconciliation (moves us to stage '4' and starts polling) rather
                    // than declaring a failure on a payment that could yet be authorized.
                    startReconciliation('storeWidgetPayment returned no result');
                } else {
                    // Already reconciling (stage '4'): keep polling with back-off.
                    handleUnconfirmed();
                }
            } else if (requestType === 'getVerifyRequired') {
                setVerifyRequired(actionResponse.success);
                setSubmitStage('2');
            }
            setLoading(false);
        }
    }, [actionResponse, loading]);

    useEffect(() => {
        if (openDialog && initStage === null) {
            // Fresh attempt (e.g. after cancel/re-entry): clear any terminal lock so a
            // new payment token cycle can Submit again.
            setStatusUnknown(false);
            setOverlapConfirm(false);
            notReadyRef.current = 0;
            setInitStage('1');
        }
    }, [openDialog])

    useEffect(() => {
        if (initStage !== '1') return;
        if (court.prePaymentNotice) {
            setOpenNotice(true);
        } else {
            setOpenDialog(true);
            setInitStage('2');
        }
    }, [initStage]);

    useEffect(() => {
        if (initStage !== '2') return;
        if (!scriptUrl) {
            getScriptUrl();
        } else {
            setInitStage('Done');
        }
    }, [initStage]);

    useEffect(() => {
        if (prepaymentShown) {
            setOpenNotice(false);
            setOpenDialog(true);
            setInitStage('2');
        }
    }, [prepaymentShown])

    useEffect(() => {
        if (scriptUrl) {
            const script = document.createElement('script');
            script .src = scriptUrl;
            script .type = 'module';
            script .async = true;
            // A module that never loads leaves <payment-instrument> un-upgraded, which
            // only shows up much later as a missing submit(). Report it at the source.
            script.onerror = () => logWidgetEvent('scriptLoadFailed', {message: scriptUrl});
            document.body.appendChild(script);
            setInitStage('Done');
            return () => {
                document.body.removeChild(script);
            }
        }
    }, [scriptUrl]);

    useEffect(() => {
        if (submitStage !== '1') return;
        if (token) {
            // Asked on every Submit rather than once when the dialog opens, so a court that
            // has been switched to verification mid-session is honoured straight away.
            getVerifyRequired();
        } else {
            resetCaptcha();
            setErrorMessage('Please complete captcha challenge and resubmit payment');
            setSubmitStage('None');
        }
    }, [submitStage])

    useEffect(() => {
        if (submitStage !== '2') return;
        if (verifyRequired) {
            setVerifyOpen(true);
        } else setSubmitStage('token');
    }, [submitStage]);

    useEffect(() => {
        if (submitStage !== 'token') return;
        getPaymentToken();
    }, [submitStage]);

    useEffect(() => {
        if (submitStage !== '3') return;
        submitPayment();
    }, [submitStage])

    useEffect(() => {
        if (submitStage !== '4') return;
        checkPaymentStatus();
    }, [submitStage, pollTick]);

    // Watch for the widget becoming usable. Polling for the method beats
    // customElements.whenDefined(): it observes the one thing submitPayment depends on,
    // so a widget that is defined but not yet fully wired cannot report itself ready.
    useEffect(() => {
        if (!openDialog || widgetReady) return;
        if (widgetCanSubmit()) {
            setWidgetReady(true);
            return;
        }
        const id = setInterval(() => {
            if (widgetCanSubmit()) {
                clearInterval(id);
                setWidgetReady(true);
            }
        }, 250);
        return () => clearInterval(id);
    }, [openDialog, widgetReady]);

    // Submit stays disabled until the widget is ready, so a widget that never loads would
    // otherwise leave the user staring at a dead button with no explanation.
    useEffect(() => {
        if (!openDialog || widgetReady) return;
        const id = setTimeout(() => {
            logWidgetEvent('widgetNeverReady', {message: 'payment-instrument has no submit() after 15s'});
            setErrorMessage(WIDGET_FAILED_MSG);
        }, 15000);
        return () => clearTimeout(id);
    }, [openDialog, widgetReady]);

    // Tests directly whether users are abandoning the page while a payment is in flight --
    // the suspected reason the reconciliation polling produced more unresolved payments
    // rather than fewer. sendBeacon still delivers the report as the page goes away.
    useEffect(() => {
        if (submitStage !== '3' && submitStage !== '4') return;
        const report = () => logWidgetEvent('pageHidden', null);
        window.addEventListener('pagehide', report);
        return () => window.removeEventListener('pagehide', report);
    }, [submitStage, pollTick]);

    return (
        <>
            { openNotice &&
                <Popup
                    open={openNotice}
                    setDone={setPrepaymentShown}
                    title="Payment Notice"
                    component={getPrePaymentNoticeComponent()}
                />
            }
            { captchaKey && !openNotice &&
                <Dialog
                    open={openDialog}
                    maxWidth='md'
                    fullWidth
                    sx={classes.dialog}
                    slotProps={{
                        paper: {sx: classes.listContainer}
                    }}
                >
                    <Backdrop sx={classes.backdrop} open={loading || noSubmit}>
                        <CircularProgress color="inherit"/>
                    </Backdrop>
                    <DialogTitle sx={{ ...classes.title, fontWeight: 700 }} aria-level="2" component="h2">Enter Payment Information</DialogTitle>
                    { errorMessage &&
                        <Alert severity='error' sx={{marginTop: '1rem', textAlign: 'left'}}>
                            <AlertTitle>Error!</AlertTitle>
                            {errorMessage}
                        </Alert>
                    }
                    { declineMessage &&
                        <Alert severity='warning' sx={{marginTop: '1rem', textAlign: 'left'}}>
                            {declineMessage}
                        </Alert>
                    }
                    { verifyOpen &&
                        <Verify
                            open={verifyOpen}
                            setOpen={setVerifyOpen}
                            paymentRequest={paymentRequest}
                            court={court}
                            handleVerificationReturned={handleVerificationReturned}
                        />
                    }
                    { openTandC &&
                        <Popup open={openTandC} setDone={closeTandC} title="Terms and Conditions" component={<ShortTandC/>} />
                    }
                    <DialogContent dividers sx={{...classes.listContainer, minHeight: '60vh', padding: small ? '0' : ''}} >
                        <Stack
                            spacing={1}
                            sx={{
                                alignItems: "center",
                                justifyContent: 'center',
                                padding: '1rem 3rem'
                            }}>
                            <Grid container spacing={1}>
                                <Grid
                                    style={{padding: '0'}}
                                    size={{
                                        xs: 6,
                                        md: 2
                                    }}>
                                    <Typography sx={{...classes.title, fontWeight: 'bold', textAlign: 'right'}}>Item(s) Subtotal:</Typography>
                                </Grid>
                                <Grid
                                    style={{padding: '0'}}
                                    size={{
                                        xs: 6,
                                        md: 2
                                    }}>
                                    <Typography color='textPrimary' sx={{textAlign: 'right', paddingRight: '2rem'}}>
                                        {feeResponse ? getCurrency(feeResponse.courtAmount) : ''}
                                    </Typography>
                                </Grid>
                                <Grid
                                    style={{padding: '0'}}
                                    size={{
                                        xs: 6,
                                        md: 2
                                    }}>
                                    <Typography color='primary' sx={{...classes.title, fontWeight: 'bold', textAlign: 'right'}}>Processing Fee:</Typography>
                                </Grid>
                                <Grid
                                    style={{padding: '0'}}
                                    size={{
                                        xs: 6,
                                        md: 2
                                    }}>
                                    <Typography color='textPrimary' sx={{textAlign: 'right', paddingRight: '2rem'}}>
                                        {feeResponse ? getCurrency(feeResponse.citepayFee) : ''}
                                    </Typography>
                                </Grid>
                                <Grid
                                    style={{padding: '0'}}
                                    size={{
                                        xs: 6,
                                        md: 2
                                    }}>
                                    <Typography sx={{...classes.title, fontWeight: 'bold', textAlign: 'right'}}>Payment Total:</Typography>
                                </Grid>
                                <Grid
                                    style={{padding: '0'}}
                                    size={{
                                        xs: 6,
                                        md: 2
                                    }}>
                                    <Typography color='textPrimary' sx={{textAlign: 'right', paddingRight: '2rem'}}>
                                        {feeResponse ? getCurrency(feeResponse.totalAmount) : ''}
                                    </Typography>
                                </Grid>
                            </Grid>
                            <payment-instrument
                                id='pi'
                                ref={widg}
                                client-id={clientId}
                                layout={court?.noChex ? 'standard:cardonly' : 'standard:multi'}
                                class='payment-instrument'
                                style={{width: '100%'}}
                            >

                            </payment-instrument>
                            <FormControlLabel
                                required
                                control={
                                    <Checkbox
                                        name="tandc"
                                        color='primary'
                                        defaultChecked={false}
                                        onChange={(e) => {setAcceptTerms(e.target.checked)}}
                                    />
                                }
                                label={ small ?
                                    <Typography component="span" sx={{fontSize: '.9rem'}}>
                                        I accept the <Link tabIndex="0" onClick={() => {setOpenTandC(true)}} onKeyDown={handleKeyDown} href="#" aria-haspopup="dialog">Payment Terms</Link>
                                    </Typography> :
                                    <Typography component="span" sx={{fontSize: '1.2rem'}}>
                                        I agree to the <Link tabIndex="0" onClick={() => {setOpenTandC(true)}} onKeyDown={handleKeyDown} href="#" aria-haspopup="dialog">CitePayUSA Terms and Conditions</Link>
                                    </Typography>
                                }
                                sx={{...classes.checkbox, marginTop: '1rem', marginLeft: '-2rem', paddingLeft: '0', lineHeight: small ? '.85rem' : ''}}
                            />
                        </Stack>
                        <div style={{margin: '.5rem 0', display: 'flex', justifyContent: 'center'}}>
                            <div>
                                <Widget />
                            </div>
                        </div>
                    </DialogContent>
                    <DialogActions sx={{paddingTop: '0', justifyContent: small ? 'center' : 'flex'}}>
                        {/* DOM order is the visual order (2.4.3); justifyContent keeps the
                            row right-aligned, which is what row-reverse used to do. */}
                        <Box component='div' sx={{display: 'flex', justifyContent: 'flex-end', margin: '1rem', padding: '0'}}>
                            <Button
                                variant="outlined"
                                color="primary"
                                startIcon={tiny ? null : <ReplayIcon/>}
                                sx={classes.squashedButton}
                                onClick={() => {
                                    setErrorMessage(null);
                                    setDeclineMessage(null);
                                    setPrepaymentShown(false);
                                    if (cancelPymt) cancelPymt();
                                    setInitStage(null);
                                    setOpenDialog(false);
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant='contained'
                                color='primary'
                                startIcon={tiny ? null : <SendTwoToneIcon />}
                                disabled={!acceptTerms || (!token && !overlapConfirm) || !widgetReady || noSubmit || statusUnknown}
                                sx={classes.squashedButton}
                                onClick={() => handleSubmit()}
                            >
                                Submit
                            </Button>
                        </Box>
                    </DialogActions>
                </Dialog>
            }
        </>
    );
}

export default ManageWidget;
